import { QueryCache, QueryClient } from "@tanstack/react-query";
import {
  BOUND_NOTE_QUERY_KEY,
  DISCUSSED_TOPICS_QUERY_KEY,
  EMAIL_QUERY_KEY,
  MEETING_QUERY_KEY,
  OBJECT_TAGS_QUERY_KEY,
  SUGGESTED_TAGGING_OPTIONS_QUERY_KEY,
  THREAD_QUERY_KEY,
  QUERY_REVALIDATE_ON_FOCUS_THRESHOLD_MS,
  USER_QUERY_KEY,
} from "@config/query";
import { useBugsnagErrorHandler } from "@hooks/useBugsnagErrorHandler";

const { notifyBugsnag } = useBugsnagErrorHandler();

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      notifyBugsnag(error, query.queryKey.join(", ") || "query");
    },
  }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

let lastRefreshTime = 0;

function refreshData() {
  void queryClient.invalidateQueries({ queryKey: [USER_QUERY_KEY] });
  void queryClient.invalidateQueries({ queryKey: [EMAIL_QUERY_KEY] });
  void queryClient.invalidateQueries({ queryKey: [THREAD_QUERY_KEY] });
  void queryClient.invalidateQueries({ queryKey: [MEETING_QUERY_KEY] });
  void queryClient.invalidateQueries({ queryKey: [BOUND_NOTE_QUERY_KEY] });
  void queryClient.invalidateQueries({ queryKey: [OBJECT_TAGS_QUERY_KEY] });
  void queryClient.invalidateQueries({ queryKey: [DISCUSSED_TOPICS_QUERY_KEY] });
  void queryClient.invalidateQueries({ queryKey: [SUGGESTED_TAGGING_OPTIONS_QUERY_KEY] });
}

function refreshIfNeeded() {
  const now = Date.now();

  // only refresh if we've seen at least one prior "activity/visibility" event
  // and enough time passed since the last one
  if (lastRefreshTime > 0 && now - lastRefreshTime > QUERY_REVALIDATE_ON_FOCUS_THRESHOLD_MS) {
    refreshData();
  }

  lastRefreshTime = now;
}

if (typeof window !== "undefined") {
  const opts: AddEventListenerOptions = { capture: true, passive: true };

  ["pointerdown", "keydown", "focus"].forEach((evt) => {
    window.addEventListener(evt, refreshIfNeeded, opts);
  });
}
